import { Form as InertiaForm, Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { TaxRuleType, TaxType } from './types.ts'
import {Button} from "@/components/ui/button.tsx";
import {ArrowLeft, Calendar, DollarSign, Edit, Plus, Trash2, TrendingUp, Users, X} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";

interface ShowProps {
  tax: TaxType
  tax_rules: TaxRuleType[]
  can_edit: boolean
  can_destroy?: boolean
  can_destroy_tax_rules?: boolean
}

const formatRuleType = (ruleType?: string): string => {
  if (ruleType === "percentage") return "Pourcentage"
  if (ruleType === "per_item") return "Par nombre d'elements"
  return "Montant fixe"
}

export default function Show({ tax, tax_rules, can_edit, can_destroy = false, can_destroy_tax_rules = false }: ShowProps) {

  const [formulaType, setFormulaType] = useState("flat_rate")
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false)
  const totalCollectedLast30Days = tax.total_collected_last_30_days ?? 0
  const totalCollectedAllTime = tax.total_collected_all_time ?? 0
  const totalTaxpayersPaying = tax.total_taxpayers_paying ?? 0
  const averageAmountPerTaxpayer = tax.average_amount_per_taxpayer ?? 0
  const activeTaxRulesCount = tax_rules.filter((rule) => rule.active).length
  const activeTaxRules = tax_rules.filter((rule) => rule.active)
  const inactiveTaxRules = tax_rules.filter((rule) => !rule.active)
  const handleDestroy = () => {
    if (!window.confirm(`Supprimer la taxe "${tax.name}" ?`)) return

    router.delete(`/admin/taxes/${tax.id}`)
  }
  const handleDestroyTaxRule = (rule: TaxRuleType) => {
    if (!window.confirm(`Supprimer la regle "${rule.code}" ?`)) return

    router.delete(`/admin/taxes/${tax.id}/tax_rules/${rule.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title={`Taxe #${tax.id}`} />

      <div className="p-8">
        <div className="mb-6">
          <Link href="/admin/taxes">
            <Button variant="ghost" size="sm" className="mb-4 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Retour
            </Button>
          </Link>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-3xl font-bold text-foreground">{tax.name}</h2>
                <span className="rounded-full bg-primary/10 px-3 py-1 font-mono text-sm font-medium text-primary">
                {tax.code}
              </span>
                {tax.active && (
                    <span className="rounded-full bg-success/10 px-3 py-1 text-sm font-medium text-success">Actif</span>
                )}
              </div>

              {/*<p className="mt-2 text-muted-foreground">{tax.description}</p>*/}
            </div>
            {(can_edit || can_destroy) && (
              <div className="flex items-center gap-2">
                {can_edit && (
                  <>
                <Button
                  className="gap-2"
                  onClick={() => {
                    setFormulaType("flat_rate")
                    setIsRuleModalOpen(true)
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Ajouter une regle
                </Button>
                <Link href={`/admin/taxes/${tax.id}/edit`}>
                  <Button className="gap-2" variant="outline">
                    <Edit className="h-4 w-4" />
                    Editer la taxe
                  </Button>
                </Link>
                  </>
                )}
                {can_destroy && (
                  <Button variant="outline" className="gap-2 text-destructive hover:text-destructive" onClick={handleDestroy}>
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-5">
          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Montant collecte (30 derniers jours)</p>
                <p className="text-xl font-bold text-foreground">
                  {totalCollectedLast30Days.toLocaleString()} FCFA
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-success/10 p-2">
                <TrendingUp className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Regles actives</p>
                <p className="text-xl font-bold text-foreground">
                  {activeTaxRulesCount.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contribuables payeurs</p>
                <p className="text-xl font-bold text-foreground">
                  {totalTaxpayersPaying.toLocaleString()}
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/10 p-2">
                <TrendingUp className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Moyenne par contribuable</p>
                <p className="text-xl font-bold text-foreground">
                  {averageAmountPerTaxpayer.toLocaleString()} FCFA
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-warning/10 p-2">
                <Calendar className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Montant collecte (global)</p>
                <p className="text-lg font-bold text-foreground">
                  {totalCollectedAllTime.toLocaleString()} FCFA
                </p>
              </div>
            </div>
          </Card>
        </div>

        <Card className="mb-6 border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">Regles de taxe actives</h2>
            <span className="rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
              {activeTaxRulesCount.toLocaleString()} actives
            </span>
          </div>

          {activeTaxRules.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune regle active.</p>
          ) : (
            <div className="space-y-3">
              {activeTaxRules.map((rule) => (
                <div key={rule.id} className="rounded-md border border-border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                        {rule.code}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatRuleType(rule.formula_type)}
                      </span>
                    </div>
                    {can_destroy_tax_rules && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Supprimer la regle ${rule.code}`}
                        onClick={() => handleDestroyTaxRule(rule)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-foreground sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <span className="text-muted-foreground">Montant</span>
                      <div className="font-medium">{rule.amount} FCFA</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Valide pendant</span>
                      <div className="font-medium">{rule.valid_for} jour(s)</div>
                    </div>
                    {rule.formula_type === "percentage" && (
                      <div>
                        <span className="text-muted-foreground">Taux (%)</span>
                        <div className="font-medium">
                          {String(rule.logic_data?.percentage_rate ?? 0)}%
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Statut</span>
                      <div className="font-medium text-success">Actif</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Cree le</span>
                      <div className="font-medium">
                        {new Date(rule.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="mb-6 border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">Regles de taxe desactivees</h2>
            <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              {inactiveTaxRules.length.toLocaleString()} inactives
            </span>
          </div>

          {inactiveTaxRules.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune regle desactivee.</p>
          ) : (
            <div className="space-y-3">
              {inactiveTaxRules.map((rule) => (
                <div key={rule.id} className="rounded-md border border-border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                        {rule.code}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatRuleType(rule.formula_type)}
                      </span>
                    </div>
                    {can_destroy_tax_rules && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Supprimer la regle ${rule.code}`}
                        onClick={() => handleDestroyTaxRule(rule)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="mt-3 grid gap-2 text-sm text-foreground sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <span className="text-muted-foreground">Montant</span>
                      <div className="font-medium">{rule.amount} FCFA</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Valide pendant</span>
                      <div className="font-medium">{rule.valid_for} jour(s)</div>
                    </div>
                    {rule.formula_type === "percentage" && (
                      <div>
                        <span className="text-muted-foreground">Taux (%)</span>
                        <div className="font-medium">
                          {String(rule.logic_data?.percentage_rate ?? 0)}%
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Statut</span>
                      <div className="font-medium text-muted-foreground">Inactif</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Cree le</span>
                      <div className="font-medium">
                        {new Date(rule.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>





        </div>

      {can_edit && isRuleModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm"
          onClick={() => setIsRuleModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-xl border border-border bg-card p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground">Nouvelle regle de taxe</h3>
                <p className="mt-1 text-sm text-muted-foreground">Configurez le montant et le type de formule.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="bg-transparent"
                onClick={() => setIsRuleModalOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <InertiaForm
              action={`/admin/taxes/${tax.id}/tax_rules`}
              method="post"
              transform={(data) => ({ tax_rule: data })}
              className="space-y-4"
              onSuccess={() => setIsRuleModalOpen(false)}
            >
              {({ errors, processing }) => (
                <>
                  <div>
                    <Label htmlFor="amount" className="mb-1 block">
                      {formulaType === "per_item" ? "Montant par element" : "Montant"}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      id="amount"
                      name="amount"
                      required
                    />
                    {errors.amount && (
                      <div className="px-3 py-2 font-medium text-red-500">
                        {Array.isArray(errors.amount) ? errors.amount.join(', ') : errors.amount}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="formula_type" className="mb-1 block">Type de formule</Label>
                    <select
                      id="formula_type"
                      name="formula_type"
                      className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[1px]"
                      value={formulaType}
                      onChange={(e) => setFormulaType(e.target.value)}
                    >
                      <option value="flat_rate">Montant fixe</option>
                      <option value="per_item">Par nombre d'elements</option>
                      <option value="percentage">Pourcentage</option>
                    </select>
                    {errors.formula_type && (
                      <div className="px-3 py-2 font-medium text-red-500">
                        {Array.isArray(errors.formula_type) ? errors.formula_type.join(', ') : errors.formula_type}
                      </div>
                    )}
                  </div>

                  {formulaType === "percentage" && (
                    <div>
                      <Label htmlFor="percentage_rate" className="mb-1 block">Taux (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        id="percentage_rate"
                        name="percentage_rate"
                        placeholder="Ex: 5"
                      />
                      {errors.percentage_rate && (
                        <div className="px-3 py-2 font-medium text-red-500">
                          {Array.isArray(errors.percentage_rate) ? errors.percentage_rate.join(', ') : errors.percentage_rate}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="valid_for" className="mb-1 block">Valide pendant (jours)</Label>
                    <Input
                      type="number"
                      min="1"
                      id="valid_for"
                      name="valid_for"
                      required
                    />
                    {errors.valid_for && (
                      <div className="px-3 py-2 font-medium text-red-500">
                        {Array.isArray(errors.valid_for) ? errors.valid_for.join(', ') : errors.valid_for}
                      </div>
                    )}
                  </div>

                  <input type="hidden" name="active" value="1" />

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsRuleModalOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing ? 'Creation...' : 'Creer la regle'}
                    </Button>
                  </div>
                </>
              )}
            </InertiaForm>
          </div>
        </div>
      )}

    </>
  )
}
